import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Label } from 'react-bootstrap';
import axios from 'axios';
import { OPTION_ENABLED, OPTION_DISABLED, OPTION_INHERITED } from 'helpers/clients';
import FormAutocomplete from 'components/forms/FormAutocomplete';

function InheritedOption(props) {
  /**
   * Expected argument properties:
   *
   * entity - usually the Client object
   * disabled - makes control inactive
   * entityPropertyName - name of the property of entity.
   *   Expect get.. and getEffective.. methods to exist, to get own and effective property values.
   * apiEndpoint - will be called to get a (would-be) inherited value.
   *   Upon switch from a defined "enabled"/"disabled" value to the "inherited"
   *   this will let display which value would be used then.
   * onChange - callback with (name, value) arguments to update the entity.
   * messageKey - key in messages.* language files,
   *   that holds a structure with the following sub-keys:
   *   "enabled", "disabled", "inherited", "title", "loading", "effective_setting"
   *   See "invoice_payment_option" messages key for reference.
   */

  const { entity, disabled, entityPropertyName, apiEndpoint, onChange, messageKey } = props;
  const { messages } = useIntl();

  const { loading, title } = messages[messageKey];
  const getOptionTranslations = messages =>
    messages?.[messageKey] || {
      enabled: OPTION_ENABLED,
      disabled: OPTION_DISABLED,
      inherited: OPTION_INHERITED,
    };

  const getOptions = messages => {
    const { enabled, disabled, inherited } = getOptionTranslations(messages);

    return [
      { label: enabled, value: OPTION_ENABLED },
      { label: disabled, value: OPTION_DISABLED },
      { label: inherited, value: OPTION_INHERITED },
    ];
  };

  const getOptionName = (messages, value) => {
    const { enabled, disabled, inherited } = getOptionTranslations(messages);
    const dictionary = {
      [OPTION_ENABLED]: enabled,
      [OPTION_DISABLED]: disabled,
      [OPTION_INHERITED]: inherited,
    };
    return dictionary[value] || '';
  };

  // check getOptionName and getEffectiveOptionName methods existence
  const ucFirst = entityPropertyName[0].toUpperCase() + entityPropertyName.slice(1);
  const getterName = `get${ucFirst}`;
  if (typeof entity[getterName] !== 'function') {
    console.error('Entity misses getter method', getterName);
    return;
  }
  const effectiveGetterName = `getEffective${ucFirst}`;
  if (typeof entity[effectiveGetterName] !== 'function') {
    console.error('Entity misses effectiveGetter method', effectiveGetterName);
    return;
  }

  const username = entity.getUsername();
  const currentValue = entity[getterName]?.();

  // dev debug
  console.log({ getterName, effectiveGetterName, username, currentValue, method: entity[effectiveGetterName] });

  const inheritedOptionStruct = entity[effectiveGetterName]?.();
  if (!inheritedOptionStruct) {
    console.error('Did not get an effective option struct');
    return;
  }

  const {
    value: storedValue,
    is_inherited: storedIsInherited,
    inherited_from_name: storedInheritedFromName,
  } = inheritedOptionStruct;

  // when was just set to "INHERITED" and no inherited value info, need to make extra request to API
  const isNeedLookup = currentValue === OPTION_INHERITED && storedIsInherited === false;

  const [isPending, setIsPending] = useState(isNeedLookup);
  // const [isInherited, setIsInherited] = useState(storedIsInherited);
  const [inheritedFrom, setInheritedFrom] = useState(storedInheritedFromName);
  const [inheritedValue, setInheritedValue] = useState(storedValue);

  useEffect(() => {
    if (!isNeedLookup) return;

    axios
      .get(apiEndpoint)
      .then(response => {
        const { data } = response;
        setInheritedFrom(data.inherited_from_name);
        setInheritedValue(data.value);
        // setIsInherited(data.is_inherited);
        setIsPending(false);
      })
      .catch(console.error);
  }, [isNeedLookup, username]);

  return (
    <>
      <div className="form-group-attached">
        <FormAutocomplete
          label={title}
          name={entityPropertyName}
          value={currentValue}
          meta={entity[`${getterName}MetaInformation`]()}
          validation={entity[`${getterName}ValidationResult`]()}
          onChange={onChange}
          options={getOptions(messages)}
          disabled={disabled}
        />
        {currentValue === OPTION_INHERITED && (
          <div className="form-group form-group-default form-autocomplete">
            {isPending ? (
              <>
                <i class="fa circle-o-notch fa-spin text-warning" />
                {loading}
              </>
            ) : (
              <>
                {loading} <Label>{getOptionName(messages, inheritedValue)}</Label> via &quot;
                {inheritedFrom}
                &quot;
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default InheritedOption;
